// Meta (Facebook) Pixel event helpers — safe no-ops until fbq is loaded (index.html base pixel).
// Veluro pixel ID lives in public/index.html (base code fires PageView globally on load).
export const VELURO_PIXEL_ID = '1434817588546475';

const PLAN_PRICES = { starter: 497, growth: 1197, command: 2497, scale: 4997, enterprise: 9997 };

export const fbTrack = (event, params, eventID) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    const p = params && Object.keys(params).length ? params : {};
    if (eventID) window.fbq('track', event, p, { eventID });
    else if (Object.keys(p).length) window.fbq('track', event, p);
    else window.fbq('track', event);
  } catch (e) { /* ignore */ }
};

// Fire an event AT MOST ONCE per unique key (persisted in localStorage) so a refresh
// or revisit never refires it. Falls back to a normal track if no key is given.
export const fbTrackOnce = (event, dedupeKey, params, eventID) => {
  if (!dedupeKey) { fbTrack(event, params, eventID); return false; }
  try {
    const k = `fb_evt_${event}_${dedupeKey}`;
    if (localStorage.getItem(k)) return false; // already fired
    fbTrack(event, params, eventID);
    localStorage.setItem(k, String(Date.now()));
    return true;
  } catch (e) {
    fbTrack(event, params, eventID);
    return true;
  }
};

export const planValue = (plan) => PLAN_PRICES[String(plan || '').toLowerCase()];

// Read Meta's click cookies. If _fbc is absent but the URL carries an fbclid, construct it
// per Meta's format (fb.1.<ms>.<fbclid>). Never hash these — they're sent raw to the API.
export const getMetaCookies = () => {
  try {
    const cookies = Object.fromEntries(document.cookie.split('; ').filter(Boolean).map((x) => {
      const i = x.indexOf('='); return [x.slice(0, i), decodeURIComponent(x.slice(i + 1))];
    }));
    let fbc = cookies._fbc;
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (!fbc && fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
    return { fbp: cookies._fbp || '', fbc: fbc || '' };
  } catch (e) { return { fbp: '', fbc: '' }; }
};

// Persist the visitor's Meta attribution cookies onto their Veluro account (first-touch),
// so trial→paid conversions sent from the server can match back to the originating ad.
// Fire-and-forget; safe to call repeatedly. `apiPost` is api.post from the caller.
export const captureMetaAttribution = (apiPost) => {
  try {
    const { fbp, fbc } = getMetaCookies();
    if (!fbp && !fbc) return;
    apiPost('/auth/meta-attribution', { fbp, fbc }).catch(() => {});
  } catch (e) { /* ignore */ }
};


// StartTrial — fires ONCE per Stripe Checkout session, ONLY after Veluro's backend has
// confirmed the trial/subscription was created (card validated). Refresh/revisit is deduped
// by the Stripe session id, so it can never fire twice.
export const trackStartTrial = (sessionId, plan) => {
  const val = planValue(plan);
  // Share the exact event_id the server uses (starttrial_<session_id>) so Meta dedupes
  // the browser pixel and the server CAPI event into ONE StartTrial.
  const eventID = sessionId ? `starttrial_${sessionId}` : undefined;
  return fbTrackOnce('StartTrial', sessionId || `plan-${plan}`, {
    value: val, currency: 'USD', content_name: plan, predicted_ltv: val,
  }, eventID);
};

// StartTrial at SIGNUP — legacy helper from the no-card era; the real StartTrial now fires when the
// Stripe trial checkout completes (trial-started page / Settings return). Kept for invited teammates. Deduped per user (never refires on login/refresh)
// and shares event_id starttrial_signup_<user_id> with the server-side CAPI event.
export const trackSignupTrial = (userId) => {
  if (!userId) return false;
  return fbTrackOnce('StartTrial', `signup_${userId}`, {
    content_name: 'free_trial', content_category: 'signup', trial_days: 7, value: 0, currency: 'USD',
  }, `starttrial_signup_${userId}`);
};

// Architecture for later: fire when a trial CONVERTS to a paid subscription (first real charge).
// The authoritative conversion happens server-side (Stripe invoice.payment_succeeded, amount > 0)
// and is recorded for Meta Conversions API. This client helper is available for any on-site
// conversion moment and is deduped by subscription id so it fires once per subscription.
// Replay server-recorded trial→paid conversions (Subscribe / Purchase) through the browser pixel
// on the user's next visit. Same event_id as the CAPI event → Meta dedupes; the browser adds
// fbp/fbc so more conversions match back to the ad that started the trial. Runs once per session.
let _replayDone = false;
export const replayPendingConversions = async (apiGet, apiPost) => {
  if (_replayDone || typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  _replayDone = true;
  try {
    const r = await apiGet('/billing/pixel-events');
    const events = r?.data?.events || [];
    if (!events.length) return;
    events.forEach((e) => fbTrack(e.event_name, {
      value: e.value, currency: e.currency || 'USD', content_name: e.plan_level, content_type: e.content_type || 'subscription',
      ...(e.predicted_ltv ? { predicted_ltv: e.predicted_ltv } : {}),
    }, e.event_id));
    await apiPost('/billing/pixel-events/ack', { event_ids: events.map((e) => e.event_id) });
  } catch (e) { /* ignore */ }
};

export const trackTrialConversion = (subscriptionId, plan) => {
  const val = planValue(plan);
  const key = subscriptionId || `plan-${plan}`;
  fbTrackOnce('Subscribe', key, { value: val, currency: 'USD', content_name: plan, content_type: 'subscription', predicted_ltv: val });
  fbTrackOnce('Purchase', key, { value: val, currency: 'USD', content_name: plan, content_type: 'subscription' });
};
